import React, { useState, useEffect, useMemo } from "react";
import {
  Row,
  Col,
  Badge,
  Modal,
  Button,
  Table,
  InputGroup,
  FormControl,
  Toast,
  ToastContainer,
  Spinner,
  Pagination,
  Image,
} from "react-bootstrap";
import {
  FaEye,
  FaUserSlash,
  FaUserCheck,
  FaBriefcase,
  FaHourglassHalf,
  FaTrash,
  FaClipboardList,
  FaClipboardCheck,
  FaRunning,
  FaGift,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaStar,
  FaUser,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExclamationTriangle,
} from "react-icons/fa";
import Card from "../../../components/Card";
import { useAuth } from "../../../components/auth/context/context";
import {
  getCustomers,
  deleteCustomer,
  updateUserStatus,
} from "../../../services/service";
import { toast } from "react-toastify";

/* badge colors */
const badgeColor = {
  Active: "bg-primary",
  Inactive: "bg-secondary",
  Blocked: "bg-danger",
  Verified: "bg-success",
  Pending: "bg-warning",
  true: "bg-success",
  false: "bg-warning",
};

// ADDED: SAFE BILINGUAL TEXT – ALWAYS RETURNS STRING
const getBilingualText = (obj, field) => {
  if (!obj || typeof obj !== "object") return "N/A";
  const value = obj[field];
  if (!value || typeof value !== "object")
    return (value || "").toString().trim() || "N/A";
  const en = value.en?.trim() || "";
  const hi = value.hi?.trim() || "";
  if (!en && !hi) return "N/A";
  if (en && hi) return `${en} (${hi})`;
  return en || hi;
};

const CustomerList = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [showTotalJobsModal, setShowTotalJobsModal] = useState(false);
  const [showActiveJobsModal, setShowActiveJobsModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [showCompletedJobsModal, setShowCompletedJobsModal] = useState(false);
  const [showPendingJobsModal, setShowPendingJobsModal] = useState(false);
  const [showBlockDeleteModal, setShowBlockDeleteModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showConfirmBlockModal, setShowConfirmBlockModal] = useState(false);
  const [customerToBlock, setCustomerToBlock] = useState(null);
  const [isBlocking, setIsBlocking] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Filter & Sorting state
  const [filterText, setFilterText] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const useDebounce = (value, delay = 500) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
  };
  const debouncedSearch = useDebounce(filterText, 500);

  // Fetch customers from API
  const fetchCustomers = async (page = 1, startOverride = null, endOverride = null) => {
    try {
      setLoading(true);
      const sDate = startOverride !== null ? startOverride : startDate;
      const eDate = endOverride !== null ? endOverride : endDate;

      const response = await getCustomers({
        page,
        itemsPerPage,
        search: debouncedSearch,
        startDate: sDate || "",
        endDate: eDate || "",
      });
      if (response.success) {
        const cleanData = response.data.users.map((customer) => {
          const jobs = customer.jobs || [];
          const completedJobs = jobs.filter((j) => j.status === "Complete");
          const activeJobs = jobs.filter((j) =>
            ["In-progress", "live"].includes(j.status)
          );
          const cancelledJobs = jobs.filter((j) => j.status === "Cancelled");
          const pendingJobs = jobs.filter((j) => j.status === "Pending");

          // === SAFE NAME LOGIC ===
          let displayName = "N/A";
          let nameEn = "N/A";
          let nameHi = "";
          let nameObj = {};

          if (customer.name) {
            if (typeof customer.name === "string") {
              displayName = customer.name;
              nameEn = customer.name;
            } else if (typeof customer.name === "object") {
              nameObj = customer.name;
              nameEn = nameObj.en || "N/A";
              nameHi = nameObj.hi || "";
              displayName = nameHi ? `${nameEn} (${nameHi})` : nameEn;
            }
          }

          return {
            id: customer._id,
            name: displayName,
            nameObj,
            nameEn,
            phone: customer.phone || "N/A",
            email: customer.email || "N/A",
            profilePic: customer.profilePic || null,
            location: getBilingualText(customer, "address"), // FIXED: Safe string
            rating: customer.rating || 0,
            jobs,
            totalJobs: jobs.length,
            activeJobs,
            pendingJobs,
            completedJobs,
            cancelledJobs,
            status: customer.isVerified ? "Active" : "Pending",
            blacklist: customer.blacklist,
            walletBalance: customer?.walletBalance || 0,
          };
        });
        setCustomers(cleanData);
        setTotalPages(response.data.totalPages || 1);
      } else {
        setError(response.message || "Failed to fetch customers");
        setShowToast(true);
      }
    } catch (err) {
      setError(err.message || "An error occurred");
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const downloadCustomerCSV = async () => {
    try {
      setDownloading(true);
      const response = await getCustomers({
        page: 1,
        itemsPerPage: 100000,
        search: debouncedSearch,
        startDate: startDate || "",
        endDate: endDate || "",
      });

      if (response.success && response.data.users) {
        const csvRows = [
          [
            "Name",
            "Phone",
            "Email",
            "Location",
            "Status",
            "TotalJobs",
            "CompletedJobs",
            "ActiveJobs",
            "PendingJobs",
            "CancelledJobs",
            "Rating",
            "WalletBalance",
            "JoinedDate",
          ],
        ];

        response.data.users.forEach((cust) => {
          // Calculate stats
          const jobs = cust.jobs || [];
          const completedJobsCount = jobs.filter(
            (j) => j.status === "Complete"
          ).length;
          const activeJobsCount = jobs.filter((j) =>
            ["In-progress", "live", "extended"].includes(j.status)
          ).length;
          const pendingJobsCount = jobs.filter(
            (j) => j.status === "Pending"
          ).length;
          const cancelledJobsCount = jobs.filter(
            (j) => j.status === "Cancelled"
          ).length;

          const totalJobsCount = jobs.length;
          const status = cust.isVerified ? "Active" : "Pending";
          const joinedDate = cust.createdAt
            ? new Date(cust.createdAt).toLocaleDateString()
            : "N/A";

          // Name logic
          let displayName = "N/A";
          if (cust.name) {
            if (typeof cust.name === "string") {
              displayName = cust.name;
            } else if (typeof cust.name === "object") {
              const nameEn = cust.name.en || "N/A";
              const nameHi = cust.name.hi || "";
              displayName = nameHi ? `${nameEn} (${nameHi})` : nameEn;
            }
          }

          const row = [
            `"${displayName}"`,
            `"${cust.phone || "N/A"}"`,
            `"${cust.email || "N/A"}"`,
            `"${getBilingualText(cust, "address")}"`,
            `"${status}"`,
            totalJobsCount,
            completedJobsCount,
            activeJobsCount,
            pendingJobsCount,
            cancelledJobsCount,
            cust.rating || 0,
            cust.walletBalance || 0,
            `"${joinedDate}"`,
          ];
          csvRows.push(row.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `customer_list_${new Date().toISOString()}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error("Failed to fetch data for CSV export");
      }
    } catch (err) {
      console.error("CSV Download Error:", err);
      toast.error("Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);
  useEffect(() => {
    if (user?.accessToken) {
      fetchCustomers(currentPage, debouncedSearch);
    }
  }, [user, currentPage, debouncedSearch]);

  // Filtering & Sorting
  // const filteredSorted = useMemo(() => {
  //   let data = customers.filter((c) => {
  //     const text = filterText.toLowerCase();
  //     const searchInName = c.name?.toLowerCase().includes(text);
  //     const searchInEn = c.nameEn?.toLowerCase().includes(text);
  //     const searchInHi = c.nameObj?.hi?.toLowerCase().includes(text);
  //     const searchInEmail = c.email?.toLowerCase().includes(text);
  //     const searchInPhone = String(c.phone || "").includes(text);
  //     const searchInLocation = c.location?.toLowerCase().includes(text);

  //     return (
  //       searchInName ||
  //       searchInEn ||
  //       searchInHi ||
  //       searchInEmail ||
  //       searchInPhone ||
  //       searchInLocation
  //     );
  //   });

  //   if (sortKey) {
  //     data.sort((a, b) => {
  //       let aVal, bVal;
  //       switch (sortKey) {
  //         case "name":
  //           aVal = a.nameEn || a.name || "";
  //           bVal = b.nameEn || b.name || "";
  //           break;
  //         case "phone":
  //           aVal = a.phone || "";
  //           bVal = b.phone || "";
  //           break;
  //         case "email":
  //           aVal = a.email || "";
  //           bVal = b.email || "";
  //           break;
  //         case "location":
  //           aVal = a.location || "";
  //           bVal = b.location || "";
  //           break;
  //         case "jobs":
  //           aVal = a.totalJobs || 0;
  //           bVal = b.totalJobs || 0;
  //           break;
  //         case "activeJobs":
  //           aVal = a.activeJobs?.length || 0;
  //           bVal = b.activeJobs?.length || 0;
  //           break;
  //         case "pendingJobs":
  //           aVal = a.pendingJobs?.length || 0;
  //           bVal = b.pendingJobs?.length || 0;
  //           break;
  //         case "completedJobs":
  //           aVal = a.completedJobs?.length || 0;
  //           bVal = b.completedJobs?.length || 0;
  //           break;
  //         case "cancelledJobs":
  //           aVal = a.cancelledJobs?.length || 0;
  //           bVal = b.cancelledJobs?.length || 0;
  //           break;
  //         case "rating":
  //           aVal = a.rating || 0;
  //           bVal = b.rating || 0;
  //           break;
  //         default:
  //           aVal = "";
  //           bVal = "";
  //       }

  //       if (typeof aVal === "string" && typeof bVal === "string") {
  //         return sortOrder === "asc"
  //           ? aVal.localeCompare(bVal)
  //           : bVal.localeCompare(aVal);
  //       }

  //       if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
  //       if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
  //       return 0;
  //     });
  //   }
  //   return data;
  // }, [customers, filterText, sortKey, sortOrder]);

  const paginatedData = customers;

  // Handlers
  const handleView = (cust) => {
    setSelectedCustomer(cust);
    setShowViewModal(true);
  };

  const handleTotalJobs = (cust) => {
    setSelectedCustomer(cust);
    setShowTotalJobsModal(true);
  };

  const handlePendingJobs = (cust) => {
    setSelectedCustomer(cust);
    setShowPendingJobsModal(true);
  };

  const handleCompletedJobs = (cust) => {
    setSelectedCustomer(cust);
    setShowCompletedJobsModal(true);
  };

  const handleActiveJobs = (cust) => {
    setSelectedCustomer(cust);
    setShowActiveJobsModal(true);
  };

  const handleCancelledClick = (cust) => {
    setSelectedCustomer(cust);
    setShowCancelledModal(true);
  };

  const toggleBlock = (id, currentStatus) => {
    const customer = customers.find((c) => c.id === id);
    setCustomerToBlock(customer);
    setIsBlocking(!currentStatus);
    setShowConfirmBlockModal(true);
  };

  const handleConfirmBlock = async () => {
    if (!customerToBlock) return;

    try {
      const newStatus = isBlocking;
      await updateUserStatus(customerToBlock.id, newStatus);

      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerToBlock.id ? { ...c, blacklist: newStatus } : c
        )
      );

      setShowConfirmBlockModal(false);
      setCustomerToBlock(null);
      if (newStatus === true) {
        toast.success("Customer has been successfully blocked.");
      } else {
        toast.success("Customer has been successfully unblocked.");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update customer status!");
      toast.error("Failed to update customer status!");
      setShowToast(true);
    }
  };

  const handleDeleteClick = (customer) => {
    setSelectedCustomer(customer);
    const hasBlockedJobs = customer.jobs.some((j) =>
      ["In-progress", "live"].includes(j.status)
    );
    if (hasBlockedJobs) {
      setShowBlockDeleteModal(true);
    } else {
      setShowConfirmDeleteModal(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCustomer(id);
      setCustomers((prev) => prev.filter((c) => c.id !== id));
      // alert("Customer deleted successfully!");
      toast.success("Customer deleted successfully!");
    } catch (err) {
      console.error("Delete failed:", err);
      // alert("Failed to delete customer!");
      toast.error("Failed to delete customer!");
    } finally {
      setShowConfirmDeleteModal(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <FaSort />;
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  // Safe initials
  const getInitials = (name) => {
    let en = "";
    if (typeof name === "string") {
      en = name;
    } else if (typeof name === "object" && name !== null) {
      en = name.en || name.hi || "";
    }
    if (!en || typeof en !== "string") return "";
    const trimmed = en.trim();
    if (!trimmed) return "";
    const words = trimmed.split(/\s+/);
    return words.length === 1
      ? words[0][0].toUpperCase()
      : words.map((w) => w[0].toUpperCase()).join("");
  };

  const getProfilePicUrl = (profilePic) => {
    if (
      !profilePic ||
      typeof profilePic !== "string" ||
      profilePic.trim() === ""
    ) {
      return null;
    }
    if (profilePic.startsWith("http")) {
      return profilePic;
    }
    const cleanPath = profilePic.startsWith("/")
      ? profilePic
      : `/${profilePic}`;
    return `${process.env.REACT_APP_IMAGE_URL}${cleanPath}`;
  };

  const Avatar = ({ src, name, size = 40 }) => {
    const [imgError, setImgError] = useState(false);
    let displayName = "Customer";
    if (typeof name === "string" && name.trim() !== "") {
      displayName = name.trim();
    } else if (typeof name === "object" && name !== null) {
      displayName = name.en || name.hi || "Customer";
    }
    const initials = displayName
      .split(/\s+/)
      .map((word) => word[0]?.toUpperCase())
      .join("")
      .slice(0, 2);

    const avatarStyle = {
      width: size,
      height: size,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#007bff",
      color: "white",
      fontWeight: "bold",
      fontSize: size > 40 ? "18px" : "14px",
    };

    return src && !imgError ? (
      <Image
        src={src}
        alt={displayName}
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          objectFit: "cover",
        }}
        onError={() => setImgError(true)}
      />
    ) : (
      <div style={avatarStyle}>{initials || "C"}</div>
    );
  };



  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={5000}
          autohide
          bg="danger"
        >
          <Toast.Header>
            <FaExclamationTriangle className="me-2" />
            <strong className="me-auto">Error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{error}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h4 className="card-title mb-0">Customer List</h4>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <FormControl
                    type="date"
                    placeholder="Start Date"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ maxWidth: "150px" }}
                  />
                  <span className="text-muted">-</span>
                  <FormControl
                    type="date"
                    placeholder="End Date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ maxWidth: "150px" }}
                  />
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCurrentPage(1);
                      fetchCustomers(1);
                    }}
                  >
                    Apply
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                      fetchCustomers(1, "", "");
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <InputGroup style={{ width: "250px" }}>
                  <FormControl
                    placeholder="Search by name, email..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  {loading && (
                    <InputGroup.Text>
                      <Spinner animation="border" size="sm" />
                    </InputGroup.Text>
                  )}
                </InputGroup>
                <Button
                  variant="primary"
                  onClick={downloadCustomerCSV}
                  disabled={downloading}
                >
                  {downloading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{" "}
                      Downloading...
                    </>
                  ) : (
                    "Download CSV"
                  )}
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="px-0" style={{ position: "relative", minHeight: "200px" }}>
              {loading && customers.length === 0 ? (
                <div className="text-center my-5">
                  <Spinner animation="border" role="status" />
                  <p className="mt-2">Loading customer data...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover style={{ opacity: loading ? 0.6 : 1 }}>
                    <thead>
                      <tr>
                        <th>Profile</th>
                        <th
                          onClick={() => handleSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaUser className="me-1" /> Name{" "}
                          {renderSortIcon("name")}
                        </th>
                        <th
                          onClick={() => handleSort("phone")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaPhone className="me-1" /> Phone{" "}
                          {renderSortIcon("phone")}
                        </th>
                        <th
                          onClick={() => handleSort("email")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaEnvelope className="me-1" /> Email{" "}
                          {renderSortIcon("email")}
                        </th>
                        <th
                          onClick={() => handleSort("location")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaMapMarkerAlt className="me-1" /> Location{" "}
                          {renderSortIcon("location")}
                        </th>
                        <th
                          onClick={() => handleSort("jobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaBriefcase className="me-1" /> Total Jobs{" "}
                          {renderSortIcon("jobs")}
                        </th>
                        <th
                          onClick={() => handleSort("activeJobs")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaRunning className="me-1" /> Active Jobs{" "}
                          {renderSortIcon("activeJobs")}
                        </th>
                        <th onClick={() => handleSort("pendingJobs")}>
                          <FaHourglassHalf className="me-1" /> Pending{" "}
                          {renderSortIcon("pendingJobs")}
                        </th>
                        <th onClick={() => handleSort("cancelledJobs")}>
                          <FaExclamationTriangle className="me-1" /> Cancelled{" "}
                          {renderSortIcon("cancelledJobs")}
                        </th>
                        <th onClick={() => handleSort("completedJobs")}>
                          <FaClipboardCheck className="me-1" /> Completed{" "}
                          {renderSortIcon("completedJobs")}
                        </th>
                        {/* <th
                        onClick={() => handleSort("referral")}
                        style={{ cursor: "pointer" }}
                      >
                        <FaGift className="me-1" /> Referral{" "}
                        {renderSortIcon("referral")}
                      </th> */}
                        <th
                          onClick={() => handleSort("rating")}
                          style={{ cursor: "pointer" }}
                        >
                          <FaStar className="me-1" /> Rating{" "}
                          {renderSortIcon("rating")}
                        </th>
                        <th>Wallet Balance</th>
                        <th>Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedData.length > 0 ? (
                        paginatedData.map((c) => (
                          <tr key={c.id}>
                            <td>
                              <Avatar
                                src={getProfilePicUrl(c.profilePic)}
                                name={
                                  c.nameObj || c.nameEn || c.name || "Customer"
                                }
                                size={40}
                              />
                            </td>
                            <td>
                              <div>
                                <strong>{c.name}</strong>
                              </div>
                              {c.nameObj?.hi && (
                                <div className="text-muted small">
                                  ({c.nameObj.hi})
                                </div>
                              )}
                            </td>
                            <td>{c.phone}</td>
                            <td>{c.email}</td>
                            <td>{c.location}</td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleTotalJobs(c)}
                              >
                                {c.totalJobs}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleActiveJobs(c)}
                              >
                                {c.activeJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handlePendingJobs(c)}
                              >
                                {c.pendingJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleCancelledClick(c)}
                              >
                                {c.cancelledJobs?.length || 0}
                              </Button>
                            </td>
                            <td>
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleCompletedJobs(c)}
                              >
                                {c.completedJobs?.length || 0}
                              </Button>
                            </td>
                            {/* <td>₹{c.referralBalance || 0}</td> */}
                            <td>
                              <Badge bg="secondary">
                                {(Number(c.rating) || 0).toFixed(1)}★
                              </Badge>
                            </td>
                            <td>₹{c?.walletBalance?.toFixed(2) || 0}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <Button
                                  variant="link"
                                  className="text-info p-0 me-2"
                                  onClick={() => handleView(c)}
                                >
                                  <FaEye size={18} />
                                </Button>
                                <Button
                                  variant="link"
                                  className="text-warning p-0 me-2"
                                  onClick={() => toggleBlock(c.id, c.blacklist)}
                                >
                                  {c.blacklist ? (
                                    <FaUserSlash size={18} />
                                  ) : (
                                    <FaUserCheck size={18} />
                                  )}
                                </Button>
                                <Button
                                  variant="link"
                                  className="text-danger p-0"
                                  onClick={() => handleDeleteClick(c)}
                                >
                                  <FaTrash size={18} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="13" className="text-center py-4">
                            {error ? (
                              <div className="text-danger">
                                <FaExclamationTriangle
                                  size={20}
                                  className="me-2"
                                />
                                Error loading customers: {error}
                              </div>
                            ) : (
                              "No customers found"
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center my-3">
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    />

                    {/* Show first page if not in window */}
                    {currentPage > 3 && (
                      <>
                        <Pagination.Item onClick={() => setCurrentPage(1)}>
                          1
                        </Pagination.Item>
                        {currentPage > 4 && <Pagination.Ellipsis disabled />}
                      </>
                    )}

                    {/* Window of pages around current page */}
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (page >= currentPage - 2 && page <= currentPage + 2) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      }
                      return null;
                    })}

                    {/* Show last page if not in window */}
                    {currentPage < totalPages - 2 && (
                      <>
                        {currentPage < totalPages - 3 && (
                          <Pagination.Ellipsis disabled />
                        )}
                        <Pagination.Item
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </Pagination.Item>
                      </>
                    )}

                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((p) => Math.min(p + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      {/* View Details Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye className="me-2" /> Customer Details
          </Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            <Row className="mb-3">
              <Col md="6">
                <p>
                  <FaUser className="me-1 text-secondary" />
                  <strong>Name:</strong> {selectedCustomer.name}
                  {selectedCustomer.nameObj?.hi && (
                    <span className="text-muted small d-block">
                      ({selectedCustomer.nameObj.hi})
                    </span>
                  )}
                </p>
                <p>
                  <strong>Customer ID:</strong> {selectedCustomer.id}
                </p>
                <p>
                  <FaPhone className="me-1 text-secondary" />
                  <strong>Phone:</strong> {selectedCustomer.phone}
                </p>
                <p>
                  <FaEnvelope className="me-1 text-secondary" />
                  <strong>Email:</strong> {selectedCustomer.email}
                </p>
                <p>
                  <FaMapMarkerAlt className="me-1 text-secondary" />
                  <strong>Location:</strong> {selectedCustomer.location}
                </p>
                <p>
                  <FaGift className="me-1 text-secondary" />
                  <strong>Referral Balance:</strong> ₹
                  {selectedCustomer.walletBalance || 0}
                </p>
              </Col>
              <Col md="6">
                <p>
                  <FaClipboardList className="me-1 text-secondary" />
                  <strong>Total Jobs:</strong>{" "}
                  {selectedCustomer.totalJobs || ""}
                </p>
                <p>
                  <FaRunning className="me-1 text-secondary" />
                  <strong>Active Jobs:</strong>{" "}
                  {selectedCustomer.activeJobs?.length || ""}
                </p>
                <p>
                  <FaHourglassHalf className="me-1 text-secondary" />
                  <strong>Pending Jobs:</strong>{" "}
                  {selectedCustomer.pendingJobs?.length || ""}
                </p>
                <p>
                  <FaExclamationTriangle className="me-1 text-secondary" />
                  <strong>Cancelled Jobs:</strong>{" "}
                  {selectedCustomer.cancelledJobs?.length || ""}
                </p>
                <p>
                  <FaStar className="me-1 text-secondary" />
                  <strong>Rating:</strong>{" "}
                  {(Number(selectedCustomer.rating) || 0).toFixed(1)}★
                </p>
                <p>
                  <FaClipboardCheck className="me-1 text-secondary" />
                  <strong>Completed Job:</strong>{" "}
                  {(
                    Number(selectedCustomer?.completedJobs?.length) || 0
                  ).toFixed(1)}
                </p>
                <p>
                  <strong>Status:</strong>{" "}
                  <span
                    className={`badge ${badgeColor[selectedCustomer.status]}`}
                  >
                    {selectedCustomer.status}
                  </span>
                </p>
              </Col>
            </Row>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Total Jobs Modal ─── */}
      <Modal
        show={showTotalJobsModal}
        onHide={() => setShowTotalJobsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaClipboardList className="me-2" /> Total Jobs
          </Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            {selectedCustomer.jobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Equipment</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Price (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.jobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>
                          {job.equipment?.name?.en || "N/A"}/
                          {job.equipment?.name?.hi || "उपकरण नहीं मिले"}
                        </td>
                        <td>
                          <Badge
                            bg={
                              job.status === "Complete"
                                ? "success"
                                : job.status === "Pending"
                                  ? "warning"
                                  : job.status === "Cancelled"
                                    ? "danger"
                                    : "secondary"
                            }
                          >
                            {job.status}
                          </Badge>
                        </td>
                        <td>
                          <Badge
                            bg={
                              job.paymentStatus === "Paid"
                                ? "success"
                                : job.paymentStatus === "Pending"
                                  ? "warning"
                                  : job.paymentStatus === "Failed"
                                    ? "danger"
                                    : "secondary"
                            }
                          >
                            {job.paymentStatus}
                          </Badge>
                        </td>
                        <td>₹{job.price?.toLocaleString() || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No jobs found for this customer.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowTotalJobsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Completed Jobs Modal */}
      <Modal
        show={showCompletedJobsModal}
        onHide={() => setShowCompletedJobsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaClipboardList className="me-2" /> Completed Jobs
          </Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            {selectedCustomer.completedJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Equipment</th>
                      <th>Completed At</th>
                      <th>Price (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.completedJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>
                          {job.equipment?.name?.en || "N/A"}/
                          {job.equipment?.name?.hi || "उपकरण नहीं मिले"}
                        </td>
                        <td>
                          {job.scheduled?.completedAt
                            ? new Date(
                              job.scheduled?.completedAt
                            ).toLocaleString("en-IN", {
                              hour12: true,
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              second: "numeric",
                            })
                            : "N/A"}
                        </td>
                        <td>₹{job.price?.toLocaleString() || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No completed jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCompletedJobsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Active Jobs Modal */}
      <Modal
        show={showActiveJobsModal}
        onHide={() => setShowActiveJobsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaRunning className="me-2" /> Active Jobs
          </Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            {(() => {
              const activeJobs = (selectedCustomer.jobs || []).filter((j) =>
                ["In-progress", "live"].includes(j.status)
              );
              return activeJobs.length ? (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Equipment</th>
                        <th>Status</th>
                        <th>Started At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeJobs.map((job, i) => (
                        <tr key={i}>
                          <td>{job.jobId}</td>
                          <td>
                            {job.equipment?.name?.en || "N/A"}/
                            {job.equipment?.name?.hi || "उपकरण नहीं मिले"}
                          </td>
                          <td>{job.status}</td>
                          <td>
                            {new Date(job.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p>No active jobs at the moment.</p>
              );
            })()}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowActiveJobsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pending Jobs Modal */}
      <Modal
        show={showPendingJobsModal}
        onHide={() => setShowPendingJobsModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHourglassHalf className="me-2" /> Pending Jobs
          </Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            {selectedCustomer.pendingJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      <th>Equipment</th>
                      <th>Status</th>
                      <th>Requested At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.pendingJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        <td>
                          {job.equipment?.name?.en || "N/A"}/
                          {job.equipment?.name?.hi || "उपकरण नहीं मिले"}
                        </td>
                        <td>{job.status || "N/A"}</td>
                        <td>
                          {job.createdAt
                            ? new Date(job.createdAt).toLocaleString()
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No pending jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPendingJobsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancelled Jobs Modal */}
      <Modal
        show={showCancelledModal}
        onHide={() => setShowCancelledModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cancelled Jobs</Modal.Title>
        </Modal.Header>
        {selectedCustomer && (
          <Modal.Body>
            {selectedCustomer.cancelledJobs?.length ? (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Job ID</th>
                      {/* <th>Customer</th> */}
                      <th>Started At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCustomer.cancelledJobs.map((job, i) => (
                      <tr key={i}>
                        <td>{job.jobId}</td>
                        {/* <td>{job.customer?.name?.en || "N/A"}</td> */}
                        <td>
                          {job.scheduled?.dateTime
                            ? new Date(job.scheduled?.dateTime).toLocaleString(
                              "en-IN",
                              {
                                hour12: true,
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                                second: "numeric",
                              }
                            )
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <p>No cancelled jobs found.</p>
            )}
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelledModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modals */}
      <Modal
        show={showBlockDeleteModal}
        onHide={() => setShowBlockDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Cannot Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This customer has jobs that are <b>In-progress</b> or <b>Live</b>. You
          cannot delete them until all jobs are completed or cancelled.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBlockDeleteModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showConfirmDeleteModal}
        onHide={() => setShowConfirmDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete customer{" "}
          <b>{selectedCustomer?.name}</b>? This will remove their account
          permanently.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleDelete(selectedCustomer.id)}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Block/Unblock Modal */}
      <Modal
        show={showConfirmBlockModal}
        onHide={() => setShowConfirmBlockModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isBlocking ? "Confirm Block" : "Confirm Unblock"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to {isBlocking ? "block" : "unblock"} customer{" "}
          <b>{customerToBlock?.name}</b>?
          <br />
          This action can be reversed anytime.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmBlockModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant={isBlocking ? "danger" : "success"}
            onClick={handleConfirmBlock}
          >
            {isBlocking ? "Block" : "Unblock"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CustomerList;
